import PropTypes from 'prop-types';
import { formatDistanceToNowStrict } from 'date-fns';
import { Avatar, Box, Card, CardMedia, ImageList, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND, SHADOW } from 'src/theme/ctmass-tokens';
import { getValidDate } from "src/utils/date-locale";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DoneIcon from "@mui/icons-material/Done";
import React from "react";
import { Preview } from "src/components/myfancy/image-preview";
import Fancybox from "src/components/myfancy/myfancybox";
import { INFO } from "src/libs/log";
import { tr } from 'src/locales/tr';

export const ChatMessage = (props) => {
    const { authorAvatar, authorName, body, attachments, createdAt, position, showUserInfo, isRead, ...other } = props;
    const mdUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

    const ago = formatDistanceToNowStrict(getValidDate(createdAt));

    const strings = body?.split("%INFO:") || [];
    if (strings.length === 3) {
        return (
            <>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexGrow: 1
                }}
                    {...other}>
                    <Card
                        sx={{
                            border: '1px solid',
                            borderColor: 'warning.main',
                            color: 'text.primary',
                            px: mdUp ? 2 : 1,
                            py: 1
                        }}
                    >
                        <Typography variant={mdUp ? "body1" : "body2"} component="div">
                            <div dangerouslySetInnerHTML={{ __html: strings[position === 'right' ? 1 : 2] }} />
                        </Typography>
                    </Card>
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: position === 'right' ? 'flex-end' : 'flex-start',
                        mt: 1,
                        px: mdUp ? 2 : 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        noWrap
                        variant="caption"
                    >
                        {tr("{{0}} ago", { 0: ago })}
                    </Typography>
                </Box></>
        )
    }

    const isHtmlBody = body && body.startsWith("%HTML:");
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: position === 'right' ? 'flex-end' : 'flex-start'
            }}
            {...other}>
            <Stack
                alignItems="flex-start"
                direction={position === 'right' ? 'row-reverse' : 'row'}
                spacing={2}
                sx={{
                    maxWidth: 500,
                    ml: position === 'right' ? 'auto' : 0,
                    mr: position === 'left' ? 'auto' : 0
                }}
            >
                {showUserInfo &&
                    <Avatar
                        src={authorAvatar || undefined}
                        sx={{
                            height: 32,
                            width: 32
                        }}
                    />}
                <Box sx={{ flexGrow: 1 }}>
                    <Card
                        elevation={0}
                        sx={{
                            backgroundColor: position === 'right' ? BRAND.navy : '#FFFFFF',
                            color: position === 'right' ? '#FFFFFF' : BRAND.ink,
                            border: position === 'right' ? 0 : `1px solid ${alpha(BRAND.navy, 0.08)}`,
                            borderRadius: position === 'right' ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                            boxShadow: position === 'right' ? `0 8px 18px ${alpha(BRAND.navy, 0.18)}` : SHADOW.sm,
                            px: 2,
                            py: 1.25,
                            '& a': { color: 'inherit', fontWeight: 600 }
                        }}
                    >
                        {showUserInfo &&
                            <Box sx={{ mb: 1 }}>
                                <Link
                                    color="inherit"
                                    sx={{ cursor: 'pointer' }}
                                    variant="caption"
                                >
                                    {authorName}
                                </Link>
                            </Box>}
                        {isHtmlBody && <Typography variant={mdUp ? "body1" : "body2"} component="div">
                            <div dangerouslySetInnerHTML={{ __html: body.replace("%HTML:", "") }} />
                        </Typography>}
                        {attachments && attachments.length > 0 && (
                            <Fancybox
                                options={{
                                    Carousel: {
                                        infinite: false,
                                    },
                                }}
                            >
                                <ImageList
                                    gap={1}
                                    variant="quilted"
                                >
                                    {attachments.map((attachment, idx) =>
                                        <a key={attachment.url || idx} data-fancybox="gallery" href={attachment.url} className={"my-fancy-link"}>
                                            <Preview attach={{ preview: attachment.url }} />
                                        </a>
                                    )}
                                </ImageList>
                            </Fancybox>
                        )}
                        {body && !isHtmlBody &&
                            (
                                <Typography
                                    color="inherit"
                                    variant={mdUp ? "body1" : "body2"}
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                    }}
                                >
                                    {body}
                                </Typography>
                            )}
                    </Card>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: position === 'right' ? 'flex-end' : 'flex-start',
                            alignItems: 'center',
                            mt: 0.5,
                            px: 1
                        }}
                    >
                        <Typography
                            color="text.secondary"
                            noWrap
                            variant="caption"
                        >
                            {tr("{{0}} ago", { 0: ago })}
                        </Typography>
                        {isRead ?
                            <DoneAllIcon sx={{ color: BRAND.green, fontSize: 16, ml: 0.75 }} />
                            :
                            <DoneIcon sx={{ color: BRAND.muted, fontSize: 16, ml: 0.75 }} />
                        }
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};

ChatMessage.propTypes = {
    authorAvatar: PropTypes.string.isRequired,
    authorName: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    attachments: PropTypes.array,
    createdAt: PropTypes.number.isRequired,
    position: PropTypes.oneOf(['left', 'right'])
};
